import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { layersMenuStyles, paper } from '@constants/mui-theme';
import { withStyles } from 'tss-react/mui';
import { getToolbarConfig, getCurrentTraffic, getCurrentBicycle, getCurrentTransit } from '@selectors/map-selector';
import { togglePois, setMapTypeId, toggleTraffic, toggleBicycle, toggleTransit, toggleLayersMenu } from '@actions/map-actions';
import PoiMarkerIcon from '@icons/poi-marker-icon';
import { textColor } from '@constants/colors';
import DefaultMapTypeIcon from '@icons/data-uri-icon/default-map-type-icon';
import SatelliteMapTypeIcon from '@icons/data-uri-icon/satellite-map-type-icon';
import TerrainMapTypeIcon from '@icons/data-uri-icon/terrain-map-type-icon';
import { FormControlLabel, Icon, Radio, RadioGroup, Switch } from '@mui';
import './layers-menu.scss';

const CompactFormControlLabel = withStyles(FormControlLabel, {
  root: {
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
    justifyContent: 'space-between'
  }
});

const RadioFormControlLabel = withStyles(FormControlLabel, {
  root: {
    margin: 0,
    width: '100%',
    justifyContent: 'space-between'
  }
});

const CompactRadio = withStyles(Radio, {
  root: {
    padding: 0
  }
});

const CompactSwitch = withStyles(Switch, (_theme, _params, classes) => ({
  root: {
    padding: 4,
    width: 40,
    height: 24
  },
  switchBase: {
    padding: 4,
    [`&.${classes.checked}`]: {
      transform: 'translateX(16px)'
    }
  },
  thumb: {
    width: 16,
    height: 16
  },
  track: {
    borderRadius: 10
  },
  checked: {}
}));

const LayersMenu = ({ small, style }) => {
  const dispatch = useDispatch();

  const traffic = useSelector(getCurrentTraffic);
  const transit = useSelector(getCurrentTransit);
  const bicycle = useSelector(getCurrentBicycle);
  const layersMenuOpen = useSelector(state => state.map.layersMenuOpen);
  const mapTypeId = useSelector(state => state.map.mapTypeId);
  const poisOn = useSelector(state => state.map.markers.poisOn);
  const toolsConfig = useSelector(getToolbarConfig);

  // Applying the poisOn style is handled by DotMapsGoogleMap, which reacts to the same
  // state.map.markers.poisOn value by swapping the map's mapId (see DOT-4736 memory notes) -
  // toggling here just needs to update the store.
  const setMapType = useCallback(event => {
    dispatch(setMapTypeId(event.target.value));
  }, [dispatch]);

  const handleTogglePois = useCallback(() => {
    dispatch(togglePois());
  }, [dispatch]);

  const handleToggleTraffic = useCallback(() => {
    dispatch(toggleTraffic());
  }, [dispatch]);

  const handleToggleTransit = useCallback(() => {
    dispatch(toggleTransit());
  }, [dispatch]);

  const handleToggleBicycle = useCallback(() => {
    dispatch(toggleBicycle());
  }, [dispatch]);

  const panelRef = useRef(null);

  // LayersMenu itself stays mounted the whole time (its parent always renders it once
  // hasMapInstance is true); only its *content* toggles via the "if (!layersMenuOpen) return
  // null" below. So this can't key off mount/unmount like the dialog fix in
  // shared/dialogs/dialog.js - it has to key off layersMenuOpen directly, with the open
  // branch's logic guarded so it only runs on the open transition, not every render.
  useEffect(() => {
    if (!layersMenuOpen) {
      return undefined; // eslint-disable-line no-undefined -- explicit no-op branch, needs a return for the other branch's cleanup function
    }
    const trigger = document.activeElement;
    const firstControl = panelRef.current?.querySelector('input, button, [tabindex]');
    firstControl?.focus();
    return () => {
      if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [layersMenuOpen]);

  const handleKeyDown = useCallback(event => {
    if (event.key === 'Escape') {
      dispatch(toggleLayersMenu());
    }
  }, [dispatch]);

  if (!layersMenuOpen) {
    return null;
  }

  const {
    traffic: trafficConfig = true,
    transit: transitConfig = true,
    bicycle: bicycleConfig = true
  } = toolsConfig;

  return (
    // Escape-to-close on a popover region is a standard pattern; it only listens, it doesn't
    // make the region itself interactive.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      aria-label="Map layers and details"
      id="layers-menu-panel"
      onKeyDown={handleKeyDown}
      ref={panelRef}
      role="region"
      styleName={`container ${small ? 'small' : ''}`}
      style={{ ...style, ...paper }}
    >
      <div aria-level="3" role="heading" styleName="header">MAP TYPE</div>
      <div>
        <RadioGroup
          name="layer-controls"
          value={mapTypeId}
          onChange={setMapType}
          style={layersMenuStyles.radioGroup.style}
        >
          <RadioFormControlLabel
            value="roadmap"
            control={<CompactRadio color="primary" size="small" />}
            label={
              <div styleName="label"><DefaultMapTypeIcon style={layersMenuStyles.menuIcons} />Default</div>
            }
            labelPlacement="start"
            style={layersMenuStyles.radio.style}
          />
          <RadioFormControlLabel
            value="hybrid"
            control={<CompactRadio color="primary" size="small" />}
            label={
              <div styleName="label"><SatelliteMapTypeIcon style={layersMenuStyles.menuIcons} />Satellite</div>
            }
            labelPlacement="start"
            style={layersMenuStyles.radio.style}
          />
          <RadioFormControlLabel
            value="terrain"
            control={<CompactRadio color="primary" size="small" />}
            label={
              <div styleName="label"><TerrainMapTypeIcon style={layersMenuStyles.menuIcons} />Terrain</div>
            }
            labelPlacement="start"
            style={layersMenuStyles.radio.style}
          />
        </RadioGroup>
      </div>
      <div aria-level="3" role="heading" styleName="header">MAP DETAILS</div>
      <div>
        <div styleName="input">
          <CompactFormControlLabel
            labelPlacement="start"
            control={
              <CompactSwitch
                checked={poisOn}
                onChange={handleTogglePois}
                color="primary"
              />
            }
            label={<div styleName="label"><PoiMarkerIcon color={textColor} />Places</div>}
          />
        </div>
        {trafficConfig &&
          <div styleName="input">
            <CompactFormControlLabel
              labelPlacement="start"
              control={
                <CompactSwitch
                  checked={traffic}
                  onChange={handleToggleTraffic}
                  color="primary"
                />
              }
              label={<div styleName="label"><Icon aria-hidden="true">traffic</Icon>Traffic</div>}
            />
          </div>
        }
        {transitConfig &&
          <div styleName="input">
            <CompactFormControlLabel
              labelPlacement="start"
              control={
                <CompactSwitch
                  checked={transit}
                  onChange={handleToggleTransit}
                  color="primary"
                />
              }
              label={<div styleName="label"><Icon aria-hidden="true">directions_transit</Icon>Transit</div>}
            />
          </div>
        }
        {bicycleConfig &&
          <div styleName="input">
            <CompactFormControlLabel
              labelPlacement="start"
              control={
                <CompactSwitch
                  checked={bicycle}
                  onChange={handleToggleBicycle}
                  color="primary"
                />
              }
              label={<div styleName="label"><Icon aria-hidden="true">directions_bike</Icon>Biking</div>}
            />
          </div>
        }
      </div>
    </div>
  );
};

LayersMenu.propTypes = {
  small: PropTypes.bool,
  style: PropTypes.object
};

export default LayersMenu;
